import AllocationsTable from '../../../AllocationsTable/AllocationsTable'

import { Navigate, Route, Routes, useMatch, useNavigate, useParams } from 'react-router-dom'
import MainContainer from 'components/layout/MainContainer/MainContainer'
import FooterBar from 'components/UI/FooterBar'
import BookendsProvider, { useBookends } from 'lib/BookendsProvider'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import HeaderTab from 'components/UI/HeaderTab/HeaderTab'
import PieChartIcon from '@mui/icons-material/PieChart';
import RequestPageOutlinedIcon from '@mui/icons-material/RequestPageOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ForumIcon from '@mui/icons-material/Forum';
import RuleIcon from '@mui/icons-material/Rule';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useCurrentUser } from 'lib/CurrentUserProvider'
import DegreePrograms from '../../DegreePrograms'
import FieldsEdit from '../../FieldsEdit'
import VendorTable from '../../VendorTable'
import DegreeProgramEdit from '../../DegreeProgramEdit'
import { useEffect } from 'react'
import InvoicePage from '../Invoice/InvoicePage'
import { useFeatures } from 'providers/FeaturesProvider'
import ExclusionRulesEdit from 'components/pages/ExclusionRulesEdit'
import TransformIcon from '@mui/icons-material/Transform';
import ClientIntegrationPage from 'components/pages/Integrations/ClientIntegrationPage'
import { store } from 'lib/DataModel'
import { InitializedRecord } from '@orbit/records'
import { getIn } from 'formik'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ClientDocumentPage from 'components/pages/Documents/ClientDocumentPage'


const tabs = {
  ALLOCATIONS: 'allocations',
  INVOICES: 'invoices',
  PROGRAMS: 'degree-programs',
  FIELDS: 'fields',
  RULES: 'rules',
  VENDORS: 'vendors',
  FORM_POSTING: 'form-posting',
  DOCUMENTS: 'documents',
} as const

function HeaderTabs({ match, pathPrefix, someContractCanPostLeads }) {
  const selectedTab = match?.params?.tab || tabs.ALLOCATIONS
  const navigate = useNavigate()
  const { setBookends } = useBookends()
  const { isFeatureFlagEnabled } = useFeatures()
  const isInvoiceEnabled = isFeatureFlagEnabled('invoice')

  // clear footer buttons on navigate so they don't remain on pages that don't reset bookend buttons
  useEffect(() => {
    setBookends({
      footer: {
        buttons: []
      }
    })
  }, [match, setBookends])

  return (
    <Tabs
      value={selectedTab}
      onChange={(_event, newValue) => navigate(`${pathPrefix}/${newValue}`)}
      sx={{ marginTop: '9px', minHeight: 76 }}
    >
      <HeaderTab
        icon={<PieChartIcon />}
        value={tabs.ALLOCATIONS}
      />
      {isInvoiceEnabled &&
        <HeaderTab
          icon={<RequestPageOutlinedIcon />}
          value={tabs.INVOICES}
        />
      }
      <HeaderTab
        icon={<AccountTreeIcon />}
        value={tabs.PROGRAMS}
      />
      <HeaderTab
        icon={<ForumIcon />}
        value={tabs.FIELDS}
      />
      <HeaderTab
        icon={<RuleIcon />}
        value={tabs.RULES}
      />
      <HeaderTab
        icon={<StorefrontIcon />}
        value={tabs.VENDORS}
      />
      {someContractCanPostLeads &&
        <HeaderTab
          icon={<TransformIcon />}
          value={tabs.FORM_POSTING}
        />
      }
      <HeaderTab
        icon={<DescriptionOutlinedIcon />}
        value={tabs.DOCUMENTS}
      />
    </Tabs>
  )
}

function ManagePageNavigation({ children, pathPrefix, someContractCanPostLeads }) {
  const match = useMatch({ path: `${pathPrefix}/:tab/*`, end: false })
  const isAllocationsPage = match?.params?.tab === 'allocations'

  return (
    <BookendsProvider>
      {/* set MainContainer to have 100% width for allocations table, but not for any other tab */}
      <MainContainer fullWidth={isAllocationsPage}>
        <Paper
          sx={{
            padding: '18px 24px',
            boxShadow: 0,
            display: 'flex',
            flexDirection: 'column',
            height: isAllocationsPage ? '100%' : undefined,
            maxWidth: '100%',
          }}
        >
          <Typography variant='h1'>Manage</Typography>

          <HeaderTabs match={match} pathPrefix={pathPrefix} someContractCanPostLeads={someContractCanPostLeads} />

          {children}
        </Paper>
      </MainContainer>

      <FooterBar/>
    </BookendsProvider>
  )
}

export default function ClientManagePage() {
  const { currentUser, currentOrganization } = useCurrentUser()
  const { clientId } = useParams()
  const pathPrefix = `/clients/${clientId}`

  const { isFeatureFlagEnabled } = useFeatures()
  const isInvoiceEnabled = isFeatureFlagEnabled('invoice')
  const contracts = clientId ? store.cache.query(q => q.findRelatedRecords({type: 'client', id: clientId}, 'contracts')) as InitializedRecord[] : []
  const someContractCanPostLeads = contracts.some(contract => getIn(contract, 'attributes.canPostLeads'))

  return (
    <ManagePageNavigation pathPrefix={pathPrefix} someContractCanPostLeads={someContractCanPostLeads} >
      <Routes>
        <Route path={tabs.ALLOCATIONS} element={<AllocationsTable hasTabNavigation={true} />} />
        {isInvoiceEnabled &&
          <Route path={tabs.INVOICES} element={<InvoicePage clientId={clientId} />} />
        }
        <Route path={tabs.PROGRAMS}>
          <Route index element={<DegreePrograms />}/>
          <Route path="new" element={<DegreeProgramEdit />}/>
          <Route path=":degreeProgramId">
            <Route index element={<DegreeProgramEdit />}/>
            <Route path="exclusion-rules" element={<ExclusionRulesEdit/>}/>
          </Route>
        </Route>
        <Route path={tabs.FIELDS} element={<FieldsEdit clientId={clientId} />}/>
        <Route path={tabs.RULES} element={<ExclusionRulesEdit/>}/>
        <Route path={tabs.VENDORS} element={<VendorTable currentOrganization={currentOrganization} currentUser={currentUser} hasTabNavigation={true} />}/>
        {someContractCanPostLeads &&
          <Route path={tabs.FORM_POSTING} element={<ClientIntegrationPage/>}/>
        }
        <Route path={tabs.DOCUMENTS} element={<ClientDocumentPage />}/>
        {/* With `manage` removed from path, this is needed to redirect non-existent pages (starting with /client/:clientId/) to the dashboard  */}
        <Route path="*" element={<Navigate to={`${pathPrefix}/dashboard`}/>}/>
      </Routes>
    </ManagePageNavigation>
  )
}
